import Link from "next/link";

/**
 * Discloses that a chart was computed without an exact birth time.
 *
 * `KundaliResponse.birth_time_known` is a CONTRACT, not a hint — its schema
 * comment says clients MUST disclose it and avoid asserting house-specific
 * claims. Until 2026-08-17 exactly one surface (the dasha calculator) honoured
 * that; every other one printed the Lagna as bare fact. 1,297 of 9,917 kundali
 * submitters over 30 days (13.1%) supply no birth time, and the ascendant
 * advances about one sign every two hours, so for those seekers the headline
 * sign is frequently wrong — the exact complaint seekers write.
 *
 * WHICH placements survive is COMPUTED, never asserted here (rules 24, 29).
 * The first version of this component reassured every seeker that "your Moon
 * sign, Sun sign and nakshatra are unaffected". `engine/time_sensitivity.py`
 * disproves that: it samples five instants across the day and reports
 * `time_sensitive_planets` — "usually just Moon near a boundary" — plus a
 * separate `moon_nakshatra_known`, because the Moon covers ~13.2 deg/day and
 * can hold one rashi while still crossing a nakshatra boundary. Hardcoding the
 * reassuring half is the same fact-invention this notice exists to correct,
 * and a false "this part is definitely right" is worse than the silence it
 * replaced. So the component renders the engine's `time_sensitivity` payload
 * and, when that payload is absent, says only what is true for every chart.
 */

/** The `time_sensitivity` block from `KundaliResponse`; absent when the time IS known. */
export interface TimeSensitivity {
  stable_planets?: string[];
  time_sensitive_planets?: string[];
  moon_nakshatra_known?: boolean;
}

const PLANET_LABELS: Record<string, string> = {
  sun: "Sun",
  moon: "Moon",
  mars: "Mars",
  mercury: "Mercury",
  jupiter: "Jupiter",
  venus: "Venus",
  saturn: "Saturn",
  rahu: "Rahu",
  ketu: "Ketu",
};

function label(planetId: string): string {
  return PLANET_LABELS[planetId] ?? planetId.replace(/_/g, " ");
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export default function BirthTimePrecisionNotice({
  subject = "ascendant",
  timeSensitivity,
  className = "",
}: {
  /** What this surface headlines, so the copy names the actual risk. */
  subject?: "ascendant" | "chart";
  /** Engine output. Omit only when the surface genuinely cannot obtain it. */
  timeSensitivity?: TimeSensitivity | null;
  className?: string;
}) {
  const headline =
    subject === "ascendant"
      ? "This ascendant is an estimate"
      : "Your Lagna and houses are estimates";

  const stable = (timeSensitivity?.stable_planets ?? []).map(label);
  const sensitive = (timeSensitivity?.time_sensitive_planets ?? []).map(label);
  const nakshatraKnown = timeSensitivity?.moon_nakshatra_known;

  return (
    <div
      role="note"
      className={`rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-ish-dark ${className}`}
    >
      <p className="font-semibold">{headline}</p>
      <p className="mt-1 leading-6">
        You did not enter a birth time, so we calculated from a fixed reference point. The ascendant
        moves to the next sign roughly every two hours, so this sign — and every house placement —
        may not be yours.
      </p>
      {stable.length > 0 && (
        <p className="mt-2 leading-6">
          <span className="font-medium">Unchanged in the sampled times: {joinList(stable)}</span> —
          these signs match at the engine’s five sampled times across your birth date. This does not
          prove that no boundary falls between samples.
        </p>
      )}
      {sensitive.length > 0 && (
        <p className="mt-2 leading-6">
          Also uncertain: {joinList(sensitive)} — that placement changes sign during your birth day.
        </p>
      )}
      {nakshatraKnown === false && (
        <p className="mt-2 leading-6">
          Your Moon nakshatra also changes during that day, so your dasha timeline is affected too.
        </p>
      )}
      <p className="mt-2 leading-6">
        <Link
          href="/research/birth-time-problem-2026/#compare-birth-times"
          className="font-medium underline"
        >
          Compare an uncertain time window
        </Link>{" "}
        to see which calculated placements change. Check birth records before treating any time as
        exact.
      </p>
    </div>
  );
}
